import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import * as WebIFC from 'web-ifc';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Loader2, MapPin, X, Crosshair } from 'lucide-react';

/**
 * Interactive 3D IFC viewer (Phase 2 "3D mode").
 * - Loads the IFC with web-ifc into a three.js scene with OrbitControls.
 * - Click a job in the dropdown, then click a surface to drop a 3D pin
 *   (raycast hit point). Pins persist as {x,y,z} world coords via onPin.
 * - Existing 3D pins are rendered as sphere markers.
 */
const PIN_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

const IfcViewer3D = ({ arrayBuffer, jobs, pins3D, activeJobId, onSelectActiveJob, onPin, onUnpin }) => {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  // Keep the latest props available to the click handler without re-binding.
  const propsRef = useRef({});
  propsRef.current = { jobs, pins3D, activeJobId, onPin };

  useEffect(() => {
    let disposed = false;
    const mount = mountRef.current;
    if (!mount || !arrayBuffer) return;

    const width = mount.clientWidth || 800;
    const height = mount.clientHeight || 500;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf1f5f9);
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 100000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    const dir = new THREE.DirectionalLight(0xffffff, 0.6);
    dir.position.set(1, 1, 1);
    scene.add(dir);
    const dir2 = new THREE.DirectionalLight(0xffffff, 0.35);
    dir2.position.set(-1, 0.5, -1);
    scene.add(dir2);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    const modelGroup = new THREE.Group();
    const pinsGroup = new THREE.Group();
    scene.add(modelGroup);
    scene.add(pinsGroup);

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    stateRef.current = { scene, camera, renderer, controls, modelGroup, pinsGroup, raycaster, pointer, mount };

    (async () => {
      try {
        const ifcApi = new WebIFC.IfcAPI();
        ifcApi.SetWasmPath(`${window.location.origin}/`, true);
        await ifcApi.Init();
        const modelID = ifcApi.OpenModel(new Uint8Array(arrayBuffer), { COORDINATE_TO_ORIGIN: true });

        ifcApi.StreamAllMeshes(modelID, (mesh) => {
          const geoms = mesh.geometries;
          for (let i = 0; i < geoms.size(); i++) {
            const pg = geoms.get(i);
            const g = ifcApi.GetGeometry(modelID, pg.geometryExpressID);
            const verts = ifcApi.GetVertexArray(g.GetVertexData(), g.GetVertexDataSize());
            const indices = ifcApi.GetIndexArray(g.GetIndexData(), g.GetIndexDataSize());
            const positions = new Float32Array(verts.length / 2);
            for (let v = 0; v < verts.length; v += 6) {
              positions[(v / 6) * 3] = verts[v];
              positions[(v / 6) * 3 + 1] = verts[v + 1];
              positions[(v / 6) * 3 + 2] = verts[v + 2];
            }
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(indices), 1));
            geometry.computeVertexNormals();
            const c = pg.color || { x: 0.6, y: 0.6, z: 0.62, w: 1 };
            const mat = new THREE.MeshLambertMaterial({
              color: new THREE.Color(c.x, c.y, c.z),
              side: THREE.DoubleSide,
              transparent: c.w < 1,
              opacity: c.w != null ? c.w : 1,
            });
            const meshObj = new THREE.Mesh(geometry, mat);
            meshObj.applyMatrix4(new THREE.Matrix4().fromArray(pg.flatTransformation));
            modelGroup.add(meshObj);
            g.delete();
          }
        });
        try { ifcApi.CloseModel(modelID); } catch (e) { /* ignore */ }

        if (disposed) return;
        if (modelGroup.children.length === 0) {
          setError('IFC model contains no renderable geometry');
          setLoading(false);
          return;
        }

        // Frame the model
        const box = new THREE.Box3().setFromObject(modelGroup);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        controls.target.copy(center);
        camera.position.set(center.x + maxDim * 0.9, center.y + maxDim * 0.9, center.z + maxDim * 1.2);
        camera.near = maxDim / 1000;
        camera.far = maxDim * 100;
        camera.updateProjectionMatrix();
        controls.update();

        stateRef.current.pinRadius = maxDim * 0.012;
        setLoading(false);
        setReady(true);
        renderPins();
      } catch (e) {
        console.error('IFC 3D load failed:', e);
        if (!disposed) { setError('Could not load this IFC in 3D. The file may be unsupported.'); setLoading(false); }
      }
    })();

    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth || width;
      const h = mount.clientHeight || height;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    // Click to pin
    const onClick = (event) => {
      const { activeJobId: aj, onPin: pinCb } = propsRef.current;
      if (!aj) return;
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(modelGroup.children, true);
      if (hits.length > 0) {
        const p = hits[0].point;
        pinCb(aj, { x: p.x, y: p.y, z: p.z });
      }
    };
    renderer.domElement.addEventListener('click', onClick);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('click', onClick);
      controls.dispose();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) { Array.isArray(obj.material) ? obj.material.forEach((m) => m.dispose()) : obj.material.dispose(); }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, [arrayBuffer]);

  // Redraw pin markers whenever pins change
  const renderPins = useCallback(() => {
    const s = stateRef.current;
    if (!s.pinsGroup) return;
    while (s.pinsGroup.children.length) {
      const c = s.pinsGroup.children.pop();
      if (c.geometry) c.geometry.dispose();
      if (c.material) c.material.dispose();
    }
    const radius = s.pinRadius || 0.2;
    (propsRef.current.pins3D || []).forEach((pin, idx) => {
      const p = pin.drawingLocation?.pin3D;
      if (!p) return;
      const geo = new THREE.SphereGeometry(radius, 20, 20);
      const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(PIN_COLORS[idx % PIN_COLORS.length]) });
      const sphere = new THREE.Mesh(geo, mat);
      sphere.position.set(p.x, p.y, p.z);
      s.pinsGroup.add(sphere);
    });
  }, []);

  useEffect(() => { if (ready) renderPins(); }, [pins3D, ready, renderPins]);

  return (
    <div className="w-full h-full relative">
      {/* Job selector + hint */}
      <div className="absolute top-3 left-3 z-20 bg-white rounded-lg shadow border border-slate-200 p-2 w-72" data-testid="ifc-3d-panel">
        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Pin a job in 3D</p>
        <select
          value={activeJobId || ''}
          onChange={(e) => onSelectActiveJob(e.target.value || null)}
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm mb-1"
          data-testid="ifc-3d-job-select"
        >
          <option value="">Select a job to place…</option>
          {(jobs || []).map((j) => (
            <option key={j.id} value={j.id}>{j.roomName || j.result?.roomName || j.type} · {j.id.slice(0, 6)}</option>
          ))}
        </select>
        <p className="text-[11px] text-slate-400 flex items-center gap-1">
          <Crosshair className="w-3 h-3" /> {activeJobId ? 'Click a surface to drop the pin' : 'Pick a job, then click the model'}
        </p>
      </div>

      {/* Placed pins list */}
      {(pins3D || []).length > 0 && (
        <div className="absolute top-3 right-3 z-20 bg-white rounded-lg shadow border border-slate-200 p-2 w-56 max-h-64 overflow-y-auto" data-testid="ifc-3d-pins-list">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">3D Pins ({pins3D.length})</p>
          {pins3D.map((j, idx) => (
            <div key={j.id} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0">
              <span className="flex items-center gap-1 min-w-0">
                <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: PIN_COLORS[idx % PIN_COLORS.length] }} />
                <span className="truncate">{j.roomName || j.result?.roomName || j.type}</span>
              </span>
              <button onClick={() => onUnpin(j.id)} className="text-slate-400 hover:text-red-500 ml-1" data-testid="ifc-3d-unpin-btn">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center text-slate-500 z-10">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading 3D model…
        </div>
      )}
      {error && !loading && (
        <div className="absolute inset-0 flex items-center justify-center text-red-500 z-10 px-6 text-center">{error}</div>
      )}
      <div ref={mountRef} className="w-full h-full" data-testid="ifc-3d-canvas" style={{ cursor: activeJobId ? 'crosshair' : 'grab' }} />
    </div>
  );
};

export default IfcViewer3D;
